import { Loader2, PlayCircle, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Toaster } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { BLD_SOURCE_KEYS, BLD_SOURCE_LABELS, type BldSourceKey } from '@/lib/bld-reports-api';
import {
  BLD_HISTORY_TIMEZONE,
  BLD_PERIOD_TYPE_LABELS,
  BLD_PERIOD_TYPE_OPTIONS,
  BLD_RUN_STATUS_CLASSES,
  BLD_RUN_STATUS_LABELS,
  BLD_RUN_STATUS_OPTIONS,
  BLD_RUN_TRIGGER_LABELS,
  formatBldDateTime,
  formatBldDuration,
  formatBldPeriodLabel,
  listBldReportHistory,
  resolveBldApiUrl,
  runBldTestGenerate,
  type BldPeriodType,
  type BldReportHistoryItem,
  type BldReportRunStatus,
  type BldTestGenerateResult,
} from '@/lib/bld-reports-history-api';
import { API_BASE_URL } from '@/lib/config';

const PAGE_SIZE = 20;
const AUTO_REFRESH_MS = 10_000;
const ALL_VALUE = 'all';

type PeriodFilter = typeof ALL_VALUE | BldPeriodType;
type StatusFilter = typeof ALL_VALUE | BldReportRunStatus;

/** Ngày chạy theo giờ Việt Nam, dạng YYYY-MM-DD (mặc định cho chạy test). */
function getDefaultTestReportDate(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: BLD_HISTORY_TIMEZONE });
}

function createAllSourcesEnabled(): Record<BldSourceKey, boolean> {
  const result = {} as Record<BldSourceKey, boolean>;
  for (const key of BLD_SOURCE_KEYS) result[key] = true;
  return result;
}

function openInNewTab(apiBaseUrl: string, url: string): void {
  window.open(resolveBldApiUrl(apiBaseUrl, url), '_blank', 'noopener,noreferrer');
}

export function ReportHistory({
  className,
  apiBaseUrl = API_BASE_URL,
  layout = 'page',
}: {
  className?: string;
  /** API base runtime của S3 Explorer (khi nhúng vào frontend host). Mặc định `API_BASE_URL`. */
  apiBaseUrl?: string;
  /**
   * `page`: trang độc lập (route `/report-history`, app standalone) — chiếm trọn màn hình
   * và tự render `Toaster`.
   * `embedded`: nội dung nhúng trong modal của frontend host — không dùng `min-h-screen`,
   * dùng `Toaster` sẵn có của `Home` và chừa chỗ bên phải cho nút đóng của Dialog.
   */
  layout?: 'page' | 'embedded';
}) {
  const { toast } = useToast();

  const [items, setItems] = useState<BldReportHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [periodType, setPeriodType] = useState<PeriodFilter>(ALL_VALUE);
  const [status, setStatus] = useState<StatusFilter>(ALL_VALUE);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [errorDetail, setErrorDetail] = useState<BldReportHistoryItem | null>(null);

  const [testOpen, setTestOpen] = useState(false);
  const [testPeriodType, setTestPeriodType] = useState<BldPeriodType>('day');
  const [testReportDate, setTestReportDate] = useState(getDefaultTestReportDate);
  const [testSources, setTestSources] = useState<Record<BldSourceKey, boolean>>(
    createAllSourcesEnabled,
  );
  const [testPersist, setTestPersist] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<BldTestGenerateResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const load = useCallback(
    async (targetPage: number, options?: { silent?: boolean }) => {
      const silent = options?.silent === true;
      if (silent) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await listBldReportHistory(apiBaseUrl, {
          periodType: periodType === ALL_VALUE ? null : periodType,
          status: status === ALL_VALUE ? null : status,
          from: from || null,
          to: to || null,
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setItems(response.data);
        setTotal(response.total);
        setError(null);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Không tải được lịch sử tạo báo cáo.';
        setError(message);
        if (silent) {
          toast({ variant: 'destructive', title: 'Lỗi làm mới', description: message });
        }
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
      }
    },
    [periodType, status, from, to, toast, apiBaseUrl],
  );

  useEffect(() => {
    void load(page);
  }, [load, page]);

  const hasRunning = useMemo(() => items.some((item) => item.status === 'running'), [items]);

  // Tự động làm mới khi còn bản ghi đang chạy; dọn interval khi unmount/hết running.
  useEffect(() => {
    if (!hasRunning) return undefined;
    const timer = window.setInterval(() => {
      void load(page, { silent: true });
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [hasRunning, load, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRunTest = async () => {
    const selectedSources = BLD_SOURCE_KEYS.filter((key) => testSources[key]);
    if (selectedSources.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Thiếu nguồn dữ liệu',
        description: 'Chọn ít nhất một nguồn dữ liệu để chạy test.',
      });
      return;
    }

    setTestRunning(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await runBldTestGenerate(apiBaseUrl, {
        periodType: testPeriodType,
        reportDate: testReportDate || undefined,
        sources: selectedSources,
        persist: testPersist,
      });
      setTestResult(result);
      if (result.errors.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Chạy test có lỗi',
          description: result.errors[0] ?? 'Xem chi tiết trong hộp thoại.',
        });
      } else {
        toast({
          title: 'Chạy test thành công',
          description: `${formatBldPeriodLabel(result.periodType, result.periodKey)} · ${formatBldDuration(result.durationMs) ?? '0ms'}`,
        });
      }
      void load(page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Không chạy được test tạo báo cáo.';
      setTestError(message);
      toast({ variant: 'destructive', title: 'Chạy test thất bại', description: message });
    } finally {
      setTestRunning(false);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
            />
          </PaginationItem>
          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .filter(
              (pageNumber) =>
                pageNumber === 1 ||
                pageNumber === totalPages ||
                Math.abs(pageNumber - page) <= 1,
            )
            .map((pageNumber, index, array) => (
              <span key={pageNumber} className="flex items-center">
                {index > 0 && array[index - 1] !== pageNumber - 1 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}
                <PaginationItem>
                  <PaginationLink
                    onClick={() => setPage(pageNumber)}
                    isActive={page === pageNumber}
                    className={loading ? 'pointer-events-none' : 'cursor-pointer'}
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              </span>
            ))}
          <PaginationItem>
            <PaginationNext
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              className={
                page === totalPages || loading ? 'pointer-events-none opacity-50' : 'cursor-pointer'
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  const isEmbedded = layout === 'embedded';

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={
          (isEmbedded
            ? 'w-full bg-background p-6 pr-12'
            : 'min-h-screen w-full bg-background p-6') + ` ${className ?? ''}`
        }
      >
        {!isEmbedded && <Toaster />}
        <div className="mx-auto w-full max-w-[1400px] space-y-4">
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold">Lịch sử tạo báo cáo</h1>
              <p className="text-sm text-muted-foreground">
                Theo dõi các lần tạo báo cáo Ban lãnh đạo, trạng thái gửi Teams và chạy test nhanh.
              </p>
              {hasRunning && (
                <p className="flex items-center gap-1 text-xs text-amber-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Đang có báo cáo chạy — tự động làm mới mỗi 10 giây.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="default"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setTestOpen(true)}
              >
                <PlayCircle className="h-4 w-4" />
                Chạy test nhanh
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                disabled={loading || refreshing}
                onClick={() => void load(page)}
              >
                <RefreshCw className={`h-4 w-4 ${loading || refreshing ? 'animate-spin' : ''}`} />
                Làm mới
              </Button>
            </div>
          </header>

          <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="bld-history-period">Loại báo cáo</Label>
              <Select
                value={periodType}
                onValueChange={(value) => {
                  setPeriodType(value as PeriodFilter);
                  setPage(1);
                }}
              >
                <SelectTrigger id="bld-history-period" className="w-[160px]">
                  <SelectValue placeholder="Tất cả" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>Tất cả</SelectItem>
                  {BLD_PERIOD_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {BLD_PERIOD_TYPE_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="bld-history-status">Trạng thái</Label>
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value as StatusFilter);
                  setPage(1);
                }}
              >
                <SelectTrigger id="bld-history-status" className="w-[160px]">
                  <SelectValue placeholder="Tất cả" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>Tất cả</SelectItem>
                  {BLD_RUN_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {BLD_RUN_STATUS_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="bld-history-from">Từ ngày</Label>
              <Input
                id="bld-history-from"
                type="date"
                className="w-[160px]"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="bld-history-to">Đến ngày</Label>
              <Input
                id="bld-history-to"
                type="date"
                className="w-[160px]"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(1);
                }}
              />
            </div>

            {(periodType !== ALL_VALUE || status !== ALL_VALUE || from !== '' || to !== '') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPeriodType(ALL_VALUE);
                  setStatus(ALL_VALUE);
                  setFrom('');
                  setTo('');
                  setPage(1);
                }}
              >
                Xoá bộ lọc
              </Button>
            )}
          </div>

          {error && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              <span>Không tải được lịch sử tạo báo cáo: {error}</span>
              <Button variant="outline" size="sm" onClick={() => void load(page)}>
                Thử lại
              </Button>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[220px]">Thời gian</TableHead>
                  <TableHead className="w-[200px]">Loại báo cáo</TableHead>
                  <TableHead className="w-[140px]">Trạng thái</TableHead>
                  <TableHead>Lỗi (nếu có)</TableHead>
                  <TableHead className="w-[220px]">Đã gửi Teams lãnh đạo</TableHead>
                  <TableHead className="w-[160px]">Xem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Đang tải lịch sử...
                      </span>
                    </TableCell>
                  </TableRow>
                )}

                {!loading && items.length === 0 && !error && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      Chưa có bản ghi nào
                    </TableCell>
                  </TableRow>
                )}

                {items.map((item) => {
                  const startedLabel = formatBldDateTime(item.startedAt) ?? item.startedAt;
                  const finishedLabel = formatBldDateTime(item.finishedAt);
                  const durationLabel = formatBldDuration(item.durationMs);
                  const sentAtLabel = formatBldDateTime(item.teamsSentAt);
                  // Chỉ lượt thành công mới có báo cáo; không tự dựng URL từ `reportId` cho lượt
                  // thất bại/đang chạy (reportId join theo kỳ có thể trỏ tới báo cáo cũ).
                  const htmlUrl = item.status === 'success' ? item.htmlUrl : null;
                  const pngUrl = item.status === 'success' ? item.pngUrl : null;

                  return (
                    <TableRow key={item.runId}>
                      <TableCell>
                        <div className="font-medium">{startedLabel}</div>
                        <div className="text-xs text-muted-foreground">
                          {finishedLabel ? `Kết thúc: ${finishedLabel}` : 'Chưa kết thúc'}
                          {durationLabel ? ` · ${durationLabel}` : ''}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge variant="outline">
                            {formatBldPeriodLabel(item.periodType, item.periodKey)}
                          </Badge>
                          <div className="flex items-center gap-1">
                            {item.trigger === 'test' && (
                              <Badge variant="secondary">{BLD_RUN_TRIGGER_LABELS.test}</Badge>
                            )}
                            {item.dryRun && (
                              <Badge variant="secondary" className="border-dashed">
                                chạy thử
                              </Badge>
                            )}
                            {item.trigger === 'manual' && !item.dryRun && (
                              <span className="text-xs text-muted-foreground">
                                {BLD_RUN_TRIGGER_LABELS.manual}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge className={BLD_RUN_STATUS_CLASSES[item.status]}>
                          {BLD_RUN_STATUS_LABELS[item.status]}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        {item.errorMessage ? (
                          <button
                            type="button"
                            onClick={() => setErrorDetail(item)}
                            className="max-w-[320px] cursor-pointer truncate text-left text-sm text-red-600 underline decoration-dotted"
                            title="Bấm để xem đầy đủ"
                          >
                            {item.errorMessage}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>

                      <TableCell>
                        {item.teamsSent ? (
                          <div className="text-sm">
                            <span className="font-medium text-green-600">Có</span>
                            {sentAtLabel && (
                              <div className="text-xs text-muted-foreground">{sentAtLabel}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Không</span>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!htmlUrl}
                            onClick={() => {
                              if (htmlUrl) openInNewTab(apiBaseUrl, htmlUrl);
                            }}
                          >
                            Xem
                          </Button>
                          {pngUrl && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (pngUrl) openInNewTab(apiBaseUrl, pngUrl);
                                  }}
                                >
                                  PNG
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Tải ảnh PNG của báo cáo</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {total > 0 ? `Tổng ${total} bản ghi` : ''}
              {refreshing ? ' · đang làm mới...' : ''}
            </p>
            {renderPagination()}
          </div>
        </div>

        <Dialog
          open={errorDetail !== null}
          onOpenChange={(open) => {
            if (!open) setErrorDetail(null);
          }}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Lỗi tạo báo cáo</DialogTitle>
              <DialogDescription>
                {errorDetail
                  ? `${formatBldPeriodLabel(errorDetail.periodType, errorDetail.periodKey)} · ${
                      formatBldDateTime(errorDetail.startedAt) ?? errorDetail.startedAt
                    }`
                  : ''}
              </DialogDescription>
            </DialogHeader>
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-xs">
              {errorDetail?.errorMessage ?? ''}
            </pre>
            <DialogFooter>
              <Button variant="outline" onClick={() => setErrorDetail(null)}>
                Đóng
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={testOpen}
          onOpenChange={(open) => {
            setTestOpen(open);
            if (!open) {
              setTestResult(null);
              setTestError(null);
            }
          }}
        >
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Chạy test nhanh</DialogTitle>
              <DialogDescription>
                Chạy thử pipeline tạo báo cáo với dữ liệu SharePoint.{' '}
                {testPersist
                  ? 'Lượt chạy và báo cáo (HTML/PNG) sẽ được lưu vào DB/S3 để kiểm tra API.'
                  : 'Chạy thử: không lưu báo cáo/ảnh, không gửi Teams — lượt chạy vẫn hiện trong lịch sử với nhãn "chạy thử".'}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="bld-test-period">Loại báo cáo</Label>
                <Select
                  value={testPeriodType}
                  onValueChange={(value) => setTestPeriodType(value as BldPeriodType)}
                >
                  <SelectTrigger id="bld-test-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BLD_PERIOD_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {BLD_PERIOD_TYPE_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="bld-test-date">Ngày chạy (báo cáo ngày lấy dữ liệu hôm trước)</Label>
                <Input
                  id="bld-test-date"
                  type="date"
                  value={testReportDate}
                  onChange={(event) => setTestReportDate(event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nguồn dữ liệu</Label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {BLD_SOURCE_KEYS.map((key) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 rounded-md border p-2 text-sm"
                    htmlFor={`bld-test-source-${key}`}
                  >
                    <Checkbox
                      id={`bld-test-source-${key}`}
                      checked={testSources[key]}
                      onCheckedChange={(checked) =>
                        setTestSources((previous) => ({ ...previous, [key]: checked === true }))
                      }
                    />
                    <span>{BLD_SOURCE_LABELS[key]}</span>
                  </label>
                ))}
              </div>
            </div>

            <label
              className="flex items-start gap-2 rounded-md border p-2 text-sm"
              htmlFor="bld-test-persist"
            >
              <Checkbox
                id="bld-test-persist"
                checked={testPersist}
                onCheckedChange={(checked) => setTestPersist(checked === true)}
              />
              <span>
                Lưu báo cáo vào DB/S3 (persist = true)
                <span className="block text-xs text-muted-foreground">
                  Ghi run + HTML/PNG vào DB/S3 để kiểm tra API xem báo cáo đã lưu; không chọn:
                  chỉ chạy thử (lượt chạy vẫn hiện trong lịch sử với nhãn "chạy thử"). Nếu cấu
                  hình đang bật gửi Teams, báo cáo test cũng sẽ được gửi.
                </span>
              </span>
            </label>

            {testError && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                {testError}
              </div>
            )}

            {testResult && (
              <div className="space-y-3 rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium">
                    {formatBldPeriodLabel(testResult.periodType, testResult.periodKey)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Thời lượng: {formatBldDuration(testResult.durationMs) ?? '0ms'}
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nguồn</TableHead>
                      <TableHead className="w-[80px]">Số dòng</TableHead>
                      <TableHead>Sheet bỏ qua</TableHead>
                      <TableHead>Nhãn thiếu</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(testResult.stats).map(([key, stat]) => (
                      <TableRow key={key}>
                        <TableCell className="align-top text-sm">
                          {BLD_SOURCE_LABELS[key as BldSourceKey] ?? key}
                          {testResult.sourceDates[key]?.backfilled && (
                            <span className="ml-1 text-xs text-amber-600">
                              (lấy ngày {testResult.sourceDates[key]?.dataDate})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="align-top text-sm">{stat.rows}</TableCell>
                        <TableCell className="align-top text-xs">
                          {stat.skippedSheets.length > 0 ? stat.skippedSheets.join(', ') : '—'}
                        </TableCell>
                        <TableCell className="align-top text-xs">
                          {stat.missingLabels.length > 0 ? stat.missingLabels.join(', ') : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {Object.keys(testResult.stats).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          Không có dữ liệu thống kê
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {testResult.warnings.length > 0 && (
                  <div className="rounded-md bg-amber-50 p-2 text-xs text-amber-700">
                    <p className="font-medium">Cảnh báo</p>
                    <ul className="list-inside list-disc">
                      {testResult.warnings.map((warning, index) => (
                        <li key={`${index}-${warning}`}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {testResult.errors.length > 0 && (
                  <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">
                    <p className="font-medium">Lỗi</p>
                    <ul className="list-inside list-disc">
                      {testResult.errors.map((item, index) => (
                        <li key={`${index}-${item}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-2">
                  <p className="text-sm font-medium">Xem trước báo cáo</p>
                  {testResult.html ? (
                    <iframe
                      title="Xem trước báo cáo"
                      srcDoc={testResult.html}
                      sandbox=""
                      className="h-[420px] w-full rounded-md border bg-white"
                    />
                  ) : testResult.htmlPreviewUrl ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (testResult.htmlPreviewUrl)
                          openInNewTab(apiBaseUrl, testResult.htmlPreviewUrl);
                      }}
                    >
                      Mở bản xem trước
                    </Button>
                  ) : (
                    <p className="text-xs text-muted-foreground">Không có bản xem trước.</p>
                  )}
                  {testResult.pngUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (testResult.pngUrl) openInNewTab(apiBaseUrl, testResult.pngUrl);
                      }}
                    >
                      Mở ảnh PNG
                    </Button>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setTestOpen(false)} disabled={testRunning}>
                Đóng
              </Button>
              <Button onClick={() => void handleRunTest()} disabled={testRunning}>
                {testRunning ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Đang chạy...
                  </span>
                ) : (
                  'Chạy test'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
